import { useEffect } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { cx } from "./cx";

export interface ModalProps {
  open: boolean;
  title: ReactNode;
  /** Mono sub-line under the title (the POC's `.sub`). */
  sub?: ReactNode;
  onClose: () => void;
  /** Rendered inside `.modal-foot`; caller supplies its own layout/buttons. */
  footer?: ReactNode;
  /** Wider dialog (980px) for content-heavy screens like match review. */
  wide?: boolean;
  /** User can drag the bottom-right corner to resize. */
  resizable?: boolean;
  children: ReactNode;
}

export function Modal({
  open,
  title,
  sub,
  onClose,
  footer,
  wide,
  resizable,
  children,
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="modal-layer"
      role="dialog"
      aria-modal="true"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section className={cx("modal", wide && "wide", resizable && "resizable")}>
        <div className="modal-head">
          <div>
            <h3>{title}</h3>
            {sub != null && <span className="sub">{sub}</span>}
          </div>
          <button className="mini" type="button" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer != null && <div className="modal-foot">{footer}</div>}
      </section>
    </div>,
    document.body
  );
}
